import type { Patient, Reference } from '@medplum/fhirtypes'
import type { CodingAnswerOption } from '@seen/core/codings'
import { today } from '@seen/core/dates'
import { formatClinicalUnit } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { unitLabel } from '@seen/core/models/observationComponents'
import type { DerivationRule } from '@seen/core/observation-logic/derivations/rules'
import { interpretScore } from '@seen/core/observation-logic/derivations/scores'
import {
  type InstrumentAnswers,
  instrumentAnswerKey,
} from '@seen/core/observation-logic/instruments/records'
import { bandSetFor } from '@seen/core/observation-logic/interpretation'
import type {
  CodedAnswer,
  ObservationValue,
} from '@seen/core/observation-logic/value'
import { OBSERVATION_MODE_LATEST } from '@seen/core/observationReferences'
import { resourceBindingConditionValue } from '@seen/core/resourceBindingRegistry'
import { panelTemplate } from '@seen/core/terminology/instruments/loincPanels'
import {
  instrumentAnswerOptions,
  instrumentItems,
  instrumentShortName,
} from '@seen/core/terminology/instruments/registry'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '@seen/core/terminology/instruments/types'
import type { ObservationPanelSummary } from '@seen/core/tiptap/schema'
import { Button, cn } from '@seen/ui'
import { IconRefresh, IconSettings } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverAnchor } from '#src/components/core/popover/PopoverAnchor.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { useIsNodeConfiguring } from '#src/components/rich-text-editor/conditional-display/useIsNodeConfiguring.ts'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import {
  type ConditionDecorationSpec,
  ObservationNodeCondition,
} from '#src/components/rich-text-editor/ObservationNode/observationNodeCondition.ts'
import {
  ObservationNodeConfigure,
  ObservationNodeConfiguring,
} from '#src/components/rich-text-editor/ObservationNode/observationNodeConfig.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ChoiceMatrix } from '#src/components/rich-text-editor/ObservationNode/panel/ChoiceMatrix.tsx'
import {
  bandColourClass,
  itemRuns,
  LABEL_CELL,
  LABEL_COLUMN,
  type LastValue,
  lastAnswers,
  lastValues,
} from '#src/components/rich-text-editor/ObservationNode/panel/instrumentItems.ts'
import { useCarriedAnswers } from '#src/components/rich-text-editor/ObservationNode/panel/useCarriedAnswers.ts'
import {
  type InstrumentCapture,
  type InstrumentPanelAttributes,
  useInstrumentCapture,
} from '#src/components/rich-text-editor/ObservationNode/panel/useInstrumentCapture.ts'
import { useLatestInstrumentTotal } from '#src/components/rich-text-editor/ObservationNode/panel/useLatestInstrumentTotal.ts'
import { usePanelTabBridge } from '#src/components/rich-text-editor/ObservationNode/panel/usePanelTabBridge.ts'
import type {
  ObservationNodeAttributes,
  ObservationRecordStatus,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useObservationCapture } from '#src/components/rich-text-editor/ObservationNode/useObservationParticipant.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'

export interface ObservationPanelOptions {
  participant?: Reference<Patient>
  recordStatus?: ObservationRecordStatus
  isTemplate?: boolean
}

// A validated tool in a note, recorded as observations: its items as a grid
// (runs sharing an answer list) or single questions, a live score, and a
// one-line summary while the note is elsewhere. Behind the
// structuredCapture flag; off, or for an unknown template, it shows the
// summary it stores.
export function ObservationPanelNodeView(props: NodeViewProps): JSX.Element {
  const enabled = useFeatureFlag(featureFlags.structuredCapture)
  const attributes = props.node.attrs as InstrumentPanelAttributes
  const template = panelTemplate(attributes)
  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex text-sm"
      data-observation-panel=""
    >
      {enabled && template ? (
        <PanelWithTemplate {...props} template={template} />
      ) : (
        <StoredSummary summary={attributes.summary} />
      )}
    </NodeViewWrapper>
  )
}

function StoredSummary({
  summary,
}: {
  summary: ObservationPanelSummary | null
}): JSX.Element {
  if (!summary) {
    return <span className="text-muted-foreground">Panel</span>
  }
  return <span>{summaryLine(summary.title ?? 'Panel', summary)}</span>
}

type SummaryParts = Pick<
  ObservationPanelSummary,
  'total' | 'band' | 'answered' | 'of' | 'scored'
>

function summaryLine(name: string, summary: SummaryParts): string {
  return `${name} · ${summaryTail(summary)}`
}

// "13 · Moderate · 9/9": the total, its band, and how much is answered. A
// measurement panel has no total, so it says how many readings it holds.
function summaryTail(summary: SummaryParts): string {
  if (summary.scored === false) {
    return readingsLabel(summary.answered)
  }
  return [
    summary.total ?? '—',
    ...(summary.band ? [summary.band.display] : []),
    `${summary.answered}/${summary.of}`,
  ].join(' · ')
}

function PanelWithTemplate(
  props: NodeViewProps & { template: InstrumentTemplate }
): JSX.Element {
  const { template, editor, getPos } = props
  const attributes = props.node.attrs as InstrumentPanelAttributes
  const options = props.extension.options as ObservationPanelOptions
  const {
    participant,
    noParticipant,
    recordStatus,
    deferred,
    template: inTemplate,
    ...capture
  } = useObservationCapture(options, editor)
  const isReadOnly = useIsReadOnly()
  const editable = capture.editable && !isReadOnly
  const frozen = attributes.mode === OBSERVATION_MODE_LATEST
  const configuring = useIsNodeConfiguring(props)
  const conditionSpec = props.decorations
    .map((decoration) => decoration.spec as Partial<ConditionDecorationSpec>)
    .find((spec) => typeof spec?.conditionText === 'string')
  const conditionText = conditionSpec?.conditionText
  const conditionCycle = !!conditionSpec?.conditionCycle
  const condition = useMemo(
    () =>
      conditionText === undefined
        ? null
        : { text: conditionText, cycle: conditionCycle },
    [conditionText, conditionCycle]
  )

  // Configuring the instrument is a template action. A note answers it.
  const configure =
    editable && inTemplate
      ? () => {
          const pos = getPos()
          if (typeof pos === 'number') {
            editor.commands.openNodeConfig(pos)
          }
        }
      : null

  return (
    <ObservationNodeCondition.Provider value={condition}>
      <ObservationNodeConfigure.Provider value={configure}>
        <ObservationNodeConfiguring.Provider value={configuring}>
          {frozen ? (
            <FrozenPanel
              template={template}
              participant={participant}
              inTemplate={inTemplate}
            />
          ) : (
            <CapturePanel
              {...props}
              template={template}
              participant={participant}
              noParticipant={noParticipant}
              inTemplate={inTemplate}
              deferred={deferred}
              editable={editable}
              recordStatus={recordStatus ?? 'final'}
            />
          )}
        </ObservationNodeConfiguring.Provider>
      </ObservationNodeConfigure.Provider>
    </ObservationNodeCondition.Provider>
  )
}

// The frame's header for the panel, read as its total: History of the
// total and Details of the total record. A template also offers Configure.
function totalAttributes(
  template: InstrumentTemplate,
  reference: string | null
): ObservationNodeAttributes {
  return {
    reference,
    display: template.title,
    codingDisplay: instrumentShortName(template),
    system: template.total?.coding.system ?? template.panel.system ?? '',
    code: template.total?.coding.code ?? template.panel.code ?? '',
    scaleType: 'Qn',
    unit: '{score}',
    answerOptions: [],
    answerValueSet: null,
    referenceResourceType: null,
    valueKind: null,
    repeats: false,
    references: null,
    groupId: null,
    autofocus: false,
  }
}

function readingsLabel(count: number): string {
  return count === 1 ? '1 reading' : `${count} readings`
}

function ScoreBadge({ summary }: { summary: SummaryParts }): JSX.Element {
  if (summary.scored === false) {
    return (
      <span className="text-muted-foreground" data-score-badge="">
        {readingsLabel(summary.answered)}
      </span>
    )
  }
  if (summary.total === null) {
    return (
      <span className="text-muted-foreground" data-score-badge="">
        {summary.answered}/{summary.of} answered
      </span>
    )
  }
  return (
    <span
      data-score-badge=""
      className={cn(
        'rounded-xs px-1 font-medium',
        bandColourClass(summary.band?.severity)
      )}
    >
      {summary.total}
      {summary.band ? ` · ${summary.band.display}` : ''}
    </span>
  )
}

function CapturePanel({
  template,
  participant,
  noParticipant,
  inTemplate,
  deferred,
  editable,
  recordStatus,
  editor,
  updateAttributes,
  node,
}: NodeViewProps & {
  template: InstrumentTemplate
  participant: Reference<Patient> | undefined
  noParticipant: boolean
  // A template's panel is answered in each note made from it.
  inTemplate: boolean
  deferred: boolean
  editable: boolean
  recordStatus: 'preliminary' | 'final'
}): JSX.Element {
  const attributes = node.attrs as InstrumentPanelAttributes
  const variable = useCallback(
    (id: string) =>
      resourceBindingConditionValue(
        id,
        editor.storage.registryVariable?.contexts ?? {}
      ),
    [editor]
  )
  const capture = useInstrumentCapture({
    template,
    attributes,
    updateAttributes,
    participant,
    recordStatus,
    variable,
    deferred,
    effectiveDateTime:
      editor.storage.registryVariable?.contexts.encounter?.period?.start,
  })
  const panelId = attributes.id
  const answers = useMemoValue(capture.answers)
  // Feeds conditions in the note this panel's answers as they are given,
  // ahead of their records. The command comes with the conditional display
  // extension, which a note may not have.
  useEffect(() => {
    if (panelId && 'setPanelAnswers' in editor.commands) {
      editor.commands.setPanelAnswers(panelId, answers)
    }
  }, [editor, panelId, answers])
  const [open, setOpen] = useState(false)
  const configure = useContext(ObservationNodeConfigure)
  const line = useRef<HTMLSpanElement | null>(null)
  const content = useRef<HTMLDivElement | null>(null)
  const focusLine = (): void => {
    line.current?.querySelector<HTMLElement>('[role="button"]')?.focus()
  }
  const tabBridge = usePanelTabBridge(open, line, content, focusLine)
  // A held answer needs no participant yet.
  const disabled = !editable || (!deferred && (noParticipant || !participant))
  const name = instrumentShortName(template)
  const visibleItems = instrumentItems(template).filter(
    (item) => !capture.hiddenKeys.has(instrumentAnswerKey(item.coding))
  )
  const { notePanels, carried, undoCarried } = useCarriedAnswers({
    editor,
    panelId,
    template,
    capture,
    visibleItems,
    open,
    disabled,
    deferred,
  })
  const last = lastAnswers(visibleItems, capture.priors, notePanels, today())
  const lastValue = lastValues(
    visibleItems,
    capture.priors,
    notePanels,
    today()
  )

  return (
    <span
      ref={line}
      className="inline-flex"
      // Captured: the strip stops its keys from reaching the line.
      onKeyDownCapture={tabBridge.onLineKeyDownCapture}
    >
      <ObservationNodeFrame
        attributes={totalAttributes(template, capture.totalReference)}
        label={name}
        required={!!attributes.required}
        participant={participant}
        valueInDetails={false}
        // A template's panel is answered in each note made from it, so a
        // click there configures it rather than opening the form.
        onClick={() => (inTemplate && configure ? configure() : setOpen(true))}
        // Still offered there, to see the form the note will show.
        openForm={inTemplate && configure ? () => setOpen(true) : undefined}
      >
        <PanelPopover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <span data-panel-summary="">{summaryTail(capture.summary)}</span>
          </PopoverAnchor>
          <PopoverContent
            ref={content}
            align="start"
            collisionPadding={16}
            aria-label={template.title}
            data-panel-grid=""
            className="flex max-h-[70vh] w-[min(56rem,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto p-3 text-sm"
            // The summary line keeps focus until the user tabs in.
            onOpenAutoFocus={(event) => event.preventDefault()}
            // The summary line and the node's own strip are part of the
            // panel, as are the node's History popover and Configure sheet:
            // focus or a press there does not close the grid.
            onInteractOutside={(event) => {
              const target = event.target
              if (
                target instanceof Element &&
                (line.current?.contains(target) ||
                  target.closest(
                    '[data-keeps-node-engaged], [data-slot="popover-content"]'
                  ))
              ) {
                event.preventDefault()
              }
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              focusLine()
            }}
            // The grid sits between the summary line and, in a template, the
            // node's strip: Done hands on to that strip, and Shift+Tab from
            // the grid's first stop goes back to the line.
            onKeyDown={tabBridge.onGridKeyDown}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-medium">{template.title}</span>
              {configure && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-panel-configure=""
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={() => {
                    setOpen(false)
                    configure()
                  }}
                >
                  <IconSettings className="size-3.5" aria-hidden />
                  Configure
                </Button>
              )}
            </span>
            {!inTemplate && noParticipant && (
              <span className="text-xs text-muted-foreground">
                Choose a participant to record
              </span>
            )}
            {template.sections.map((section) => {
              const shown = section.items.filter(
                (item) =>
                  !capture.hiddenKeys.has(instrumentAnswerKey(item.coding))
              )
              if (!shown.length) {
                return null
              }
              return (
                // Every row, in a grid or on its own, shares one label
                // column and one answer edge, with no gap between them.
                <div key={section.id} className="flex flex-col">
                  {section.title && (
                    <span className="pt-1 pb-0.5 text-xs text-muted-foreground">
                      {section.title}
                    </span>
                  )}
                  {itemRuns(shown).map((run) =>
                    run.kind === 'matrix' ||
                    instrumentAnswerOptions(run.item).length ? (
                      <ChoiceMatrix
                        key={instrumentAnswerKey(
                          (run.kind === 'matrix' ? run.items[0] : run.item)
                            ?.coding ?? {}
                        )}
                        items={run.kind === 'matrix' ? run.items : [run.item]}
                        wrap={run.kind === 'single'}
                        answers={capture.answers}
                        last={last}
                        disabled={disabled}
                        onAnswer={(item, option) =>
                          capture.answer(
                            item,
                            option ? codedValue(option) : null
                          )
                        }
                      />
                    ) : (
                      <SingleItem
                        key={instrumentAnswerKey(run.item.coding)}
                        item={run.item}
                        answers={capture.answers}
                        last={lastValue[instrumentAnswerKey(run.item.coding)]}
                        disabled={disabled}
                        onAnswer={(value) => capture.answer(run.item, value)}
                        calculated={calculatedState(capture, run.item)}
                      />
                    )
                  )}
                </div>
              )
            })}
            {carried && (
              <span
                data-carried-over=""
                className="text-xs text-muted-foreground"
              >
                {carried.keys.length} answer
                {carried.keys.length === 1 ? '' : 's'} carried over from{' '}
                {carried.from.join(', ')} ·{' '}
                <button
                  type="button"
                  className="underline hover:no-underline"
                  onClick={undoCarried}
                >
                  Undo
                </button>
              </span>
            )}
            <span className="self-end">
              <ScoreBadge summary={capture.summary} />
            </span>
            {template.copyright && (
              <span className="text-[11px] text-muted-foreground">
                {template.copyright}
              </span>
            )}
            <span className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-panel-done=""
                onClick={() => {
                  setOpen(false)
                  focusLine()
                }}
              >
                Done
              </Button>
            </span>
          </PopoverContent>
        </PanelPopover>
      </ObservationNodeFrame>
    </span>
  )
}

// The grid's popover, reporting itself open to the node's frame so the
// strip stays up while it is.
function PanelPopover({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}): JSX.Element {
  const report = useReportPopoverOpen(false)
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        report(next)
        onOpenChange(next)
      }}
      modal={false}
    >
      {children}
    </Popover>
  )
}

function codedValue(option: CodingAnswerOption): ObservationValue {
  const coding: CodedAnswer = { code: option.code, display: option.display }
  if (option.system) {
    coding.system = option.system
  }
  return { kind: 'coding', value: [coding] }
}

interface CalculatedState {
  rule: DerivationRule
  overridden: boolean
  recalculate: () => void
}

function calculatedState(
  capture: InstrumentCapture,
  item: InstrumentItem
): CalculatedState | undefined {
  const key = instrumentAnswerKey(item.coding)
  const rule = capture.derived.get(key)
  return rule
    ? {
        rule,
        overridden: capture.overrides.has(key),
        recalculate: () => capture.recalculate(item),
      }
    : undefined
}

// A question answered with a number or a line of text, in the same columns
// as the answer grids, with how it is scored under its label. A number out
// of the item's range is refused, not saved. An earlier answer from another
// day is offered while the row is empty. A calculated item fills itself
// from the others; a value typed over it holds until Recalculate.
function SingleItem({
  item,
  answers,
  last,
  disabled,
  onAnswer,
  calculated,
}: {
  item: InstrumentItem
  answers: InstrumentAnswers
  last?: LastValue
  disabled: boolean
  onAnswer: (value: ObservationValue | null) => void
  calculated?: CalculatedState
}): JSX.Element {
  const value = answers[instrumentAnswerKey(item.coding)]
  const band =
    calculated && value?.kind === 'number'
      ? (() => {
          const bands = bandSetFor(item.coding)?.bands
          return bands ? interpretScore(value.value, bands) : undefined
        })()
      : undefined
  const method = calculated?.rule.method
  const number = item.scaleType === 'Qn'
  const range = number ? item.range : undefined
  const current =
    value?.kind === 'number' || value?.kind === 'string'
      ? String(value.value)
      : ''
  const [invalid, setInvalid] = useState(false)
  const hintId = useId()
  const errorId = useId()
  const save = (text: string): void => {
    if (text === current) {
      setInvalid(false)
      return
    }
    const parsed = Number(text)
    if (
      text &&
      number &&
      (Number.isNaN(parsed) ||
        (range && (parsed < range.min || parsed > range.max)))
    ) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    if (!text) {
      onAnswer(null)
      return
    }
    if (!number) {
      onAnswer({ kind: 'string', value: text })
      return
    }
    const answer: ObservationValue = { kind: 'number', value: parsed }
    if (item.unit) {
      answer.unit = item.unit
    }
    onAnswer(answer)
  }
  return (
    <div
      className="grid text-sm"
      style={{ gridTemplateColumns: `${LABEL_COLUMN} minmax(0, 1fr)` }}
      data-single-item=""
    >
      <span className={cn('flex flex-col px-0.5 py-0.5', LABEL_CELL)}>
        {item.label}
        {item.hint && (
          <span id={hintId} className="text-xs text-muted-foreground">
            {item.hint}
          </span>
        )}
      </span>
      <span className="px-0.5 py-0.5">
        <input
          key={current}
          aria-label={item.label}
          aria-describedby={
            [item.hint ? hintId : '', invalid ? errorId : '']
              .filter(Boolean)
              .join(' ') || undefined
          }
          aria-invalid={invalid || undefined}
          type={number ? 'number' : 'text'}
          defaultValue={current}
          min={range?.min}
          max={range?.max}
          placeholder={range ? `${range.min}–${range.max}` : undefined}
          disabled={disabled}
          onBlur={(event) => save(event.target.value.trim())}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              save(event.currentTarget.value.trim())
            }
          }}
          className={cn(
            'm-0.5 h-6 w-24 rounded-md border px-1 placeholder:text-placeholder',
            invalid && 'border-destructive'
          )}
        />
        {range && !item.unit ? (
          <span className="ml-1 text-xs text-foreground-secondary">
            / {range.max}
          </span>
        ) : null}
        {item.unit ? (
          <span className="ml-1 text-xs text-foreground-secondary">
            {formatClinicalUnit(unitLabel(item.unit))}
          </span>
        ) : null}
        {invalid && range ? (
          <span
            id={errorId}
            data-range-error=""
            className="ml-2 text-xs text-destructive"
          >
            Enter {range.min}–{range.max}
          </span>
        ) : null}
        {!current && !invalid && last && !calculated ? (
          <button
            type="button"
            data-last-value=""
            disabled={disabled}
            title="Use the last answer"
            className="ml-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none"
            onClick={() => save(String(last.value))}
          >
            Last {last.value}
            {last.day ? ` · ${last.day}` : ''}
          </button>
        ) : null}
        {band && (
          <span data-calculated-band="" className="ml-1 text-xs">
            {band.display}
          </span>
        )}
        {calculated &&
          (calculated.overridden ? (
            <button
              type="button"
              aria-label="Recalculate"
              title="Set by hand · Recalculate"
              data-calculated-override=""
              disabled={disabled}
              className="ml-1 inline-flex align-middle text-muted-foreground hover:text-foreground disabled:pointer-events-none"
              onClick={calculated.recalculate}
            >
              <IconRefresh className="size-3.5" aria-hidden />
            </button>
          ) : (
            <span
              data-calculated=""
              title={[method?.display, method?.formula]
                .filter(Boolean)
                .join(': ')}
              className="ml-1 text-xs text-muted-foreground"
            >
              Calculated
            </span>
          ))}
      </span>
    </div>
  )
}

// The latest total and band, for a panel frozen to the latest value.
function FrozenPanel({
  template,
  participant,
  inTemplate,
}: {
  template: InstrumentTemplate
  participant: Reference<Patient> | undefined
  // In a template the latest total is resolved in each note made from it.
  inTemplate: boolean
}): JSX.Element {
  const { total } = useLatestInstrumentTotal(
    participant,
    template.total?.coding
  )
  const configure = useContext(ObservationNodeConfigure)
  const name = instrumentShortName(template)
  const band = total?.toFhirResource().interpretation?.[0]?.text
  return (
    <span className="inline-flex">
      <ObservationNodeFrame
        attributes={totalAttributes(
          template,
          total?.reference.reference ?? null
        )}
        label={name}
        participant={participant}
        valueInDetails={false}
        noHover
        // In a template the line opens the panel's configuration.
        onClick={inTemplate ? (configure ?? undefined) : undefined}
      >
        <span
          data-panel-latest=""
          title="Latest recorded total"
          className="rounded-xs bg-muted/60 px-0.5 underline decoration-dotted underline-offset-2"
        >
          {inTemplate
            ? 'Latest'
            : total?.valueQuantity === undefined
              ? 'No value recorded'
              : `${total.valueQuantity}${band ? ` · ${band}` : ''}`}
        </span>
      </ObservationNodeFrame>
    </span>
  )
}
